/**
 * Drafts / Sent tabs with Sent sub-filters as segmented control.
 * Single row, minimal, follows established tab patterns.
 */

import type { FC } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '@/lib/utils';
import type { SentSection } from '../../shared/types';

interface TabsRowProps {
  activeTab: 'drafts' | 'sent';
  draftCount: number;
  sentCount: number;
  sentCountBySection: Record<SentSection, number>;
  sentSection: SentSection;
  onSwitchToDrafts: () => void;
  onSwitchToSent: () => void;
  onSentSectionChange?: (section: SentSection) => void;
  showPlusOne: boolean;
}

export const TabsRow: FC<TabsRowProps> = ({
  activeTab,
  draftCount,
  sentCount,
  sentCountBySection,
  sentSection,
  onSwitchToDrafts,
  onSwitchToSent,
  onSentSectionChange,
  showPlusOne,
}) => (
  <div className="flex items-center gap-6">
    <div className="flex items-center gap-1">
      <button
        type="button"
        onClick={onSwitchToDrafts}
        className={cn(
          'rounded-md px-3 py-1.5 text-xs font-semibold uppercase tracking-wider transition-colors',
          activeTab === 'drafts'
            ? 'bg-primary/15 text-primary'
            : 'text-muted-foreground hover:bg-muted/30 hover:text-foreground',
        )}
      >
        Drafts ({draftCount})
      </button>
      <button
        type="button"
        onClick={onSwitchToSent}
        className={cn(
          'relative rounded-md px-3 py-1.5 text-xs font-semibold uppercase tracking-wider transition-colors',
          activeTab === 'sent'
            ? 'bg-success/15 text-success'
            : 'text-muted-foreground hover:bg-muted/30 hover:text-foreground',
        )}
      >
        Sent ({sentCount})
        <AnimatePresence>
          {showPlusOne && (
            <motion.span
              key="plus-one"
              initial={{ opacity: 0, scale: 0.5 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0 }}
              className="absolute -right-1 -top-0.5 rounded-full bg-success px-1 text-[10px] font-bold text-success-foreground"
            >
              +1
            </motion.span>
          )}
        </AnimatePresence>
      </button>
    </div>
    {activeTab === 'sent' && onSentSectionChange && (
      <div
        role="tablist"
        aria-label="Sent status filter"
        className="inline-flex rounded-md bg-muted/50 p-0.5"
      >
        {(['sent', 'scheduled', 'killed'] as const).map((id) => {
          const isActive = sentSection === id;
          const styles = {
            sent: isActive
              ? 'bg-success/15 text-success shadow-sm'
              : 'text-muted-foreground hover:bg-success/10 hover:text-success',
            scheduled: isActive
              ? 'bg-amber-500/15 text-amber-600 shadow-sm dark:text-amber-400'
              : 'text-muted-foreground hover:bg-amber-500/10 hover:text-amber-600 dark:hover:text-amber-400',
            killed: isActive
              ? 'bg-destructive/15 text-destructive shadow-sm'
              : 'text-muted-foreground hover:bg-destructive/10 hover:text-destructive',
          };
          return (
            <button
              key={id}
              type="button"
              role="tab"
              aria-selected={isActive}
              onClick={() => onSentSectionChange(id)}
              className={cn('rounded px-3 py-1.5 text-xs font-medium transition-colors', styles[id])}
            >
              {id === 'sent' ? 'Sent' : id === 'scheduled' ? 'Scheduled' : 'Killed'} (
              {sentCountBySection[id]})
            </button>
          );
        })}
      </div>
    )}
  </div>
);
